import React, { useMemo } from 'react';

const short = (iso) => { if (!iso) return ''; const p = String(iso).split('-'); return `${p[1]}/${p[2]}`; };
const full = (iso) => { if (!iso) return ''; const p = String(iso).split('-'); return `${p[1]}/${p[2]}/${p[0]}`; };

const sortTeamName = (a, b) => {
  const re = /^PM(\d+)$/;
  const ma = a.team_name.match(re), mb = b.team_name.match(re);
  if (ma && mb) return parseInt(ma[1]) - parseInt(mb[1]);
  if (ma) return -1; if (mb) return 1;
  return a.team_name.localeCompare(b.team_name);
};

// Agrupa os labor_bills de UMA equipe por semana de pagamento (week_ending),
// replicando a mesma matemática do backend (lucro depende da porcentagem, backcharge
// reduz Total Final e aumenta Lucro).
export const buildTeamWeekly = (team, weekMetaMap) => {
  const pct = team.percentage || 0;
  const byWeek = {};
  (team.labor_bills || []).forEach((b) => {
    const wk = b.week_ending || '';
    if (!byWeek[wk]) {
      byWeek[wk] = { week_ending: wk, trabalhos: 0, total_labor: 0, total_expenses: 0, backcharge_total: 0, adicional_total: 0, _workFinal: 0 };
    }
    const g = byWeek[wk];
    if (b.type === 'backcharge') {
      g.backcharge_total += (b.valor || 0);
    } else if (b.type === 'adicional') {
      g.adicional_total += (b.valor || 0);
    } else {
      g.trabalhos += 1;
      g.total_labor += (b.labor_total || 0);
      g.total_expenses += (b.expenses || 0);
      g._workFinal += (b.final_value || 0);
    }
  });
  return Object.values(byWeek).map((g) => {
    const total_final = g._workFinal - g.backcharge_total + g.adicional_total;
    const base_lucro = pct === 0 ? (g.total_labor - g.total_expenses) : (g.total_labor - g._workFinal);
    const lucro_empresa = base_lucro + g.backcharge_total - g.adicional_total;
    const meta = (weekMetaMap && weekMetaMap[g.week_ending]) || {};
    return {
      week_ending: g.week_ending,
      work_start: meta.work_start,
      work_end: meta.work_end,
      trabalhos: g.trabalhos,
      total_labor: g.total_labor,
      total_expenses: g.total_expenses,
      backcharge_total: g.backcharge_total,
      adicional_total: g.adicional_total,
      total_final,
      lucro_empresa,
      percentage: pct,
    };
  }).sort((a, b) => (a.week_ending || '').localeCompare(b.week_ending || ''));
};

const buildColumns = (fields, isAdmin) => {
  const cols = [];
  if (fields.trabalhos) cols.push({ key: 'trabalhos', label: 'Trab', money: false, admin: false });
  if (fields.total_labor) cols.push({ key: 'total_labor', label: 'Labor', money: true, admin: true });
  if (fields.despesas) cols.push({ key: 'total_expenses', label: 'Despesas', money: true, admin: true });
  if (fields.backcharge) cols.push({ key: 'backcharge_total', label: 'Backcharge / Adicional', money: true, admin: true, neg: true });
  if (fields.total_final) cols.push({ key: 'total_final', label: 'Total Final', money: true, admin: true });
  if (fields.lucro) cols.push({ key: 'lucro_empresa', label: 'Lucro', money: true, admin: true });
  return cols.filter((c) => isAdmin || !c.admin);
};

const CrewsWeeklyView = ({
  teams = [],
  selectedTeams = [],
  weeksBreakdown = [],
  viewMode = 'cards',
  cardsFields = {},
  tableFields = {},
  rankingFields = {},
  sortBy = 'total_final',
  sortOrder = 'desc',
  isAdmin = false,
  formatMoney = (v) => v,
}) => {
  const weekMetaMap = useMemo(() => {
    const m = {};
    (weeksBreakdown || []).forEach((w) => { m[w.week_ending] = { work_start: w.work_start, work_end: w.work_end }; });
    return m;
  }, [weeksBreakdown]);

  const orderedWeeks = useMemo(() => (
    (weeksBreakdown || []).map((w) => w.week_ending).filter(Boolean).sort((a, b) => a.localeCompare(b))
  ), [weeksBreakdown]);

  const activeTeams = useMemo(() => (
    teams
      .filter((t) => selectedTeams.includes(t.team_name))
      .filter((t) => isAdmin || t.team_name !== 'PM1')
      .sort((a, b) => {
        const av = a[sortBy] || 0, bv = b[sortBy] || 0;
        if (av !== bv) return sortOrder === 'desc' ? bv - av : av - bv;
        return sortTeamName(a, b);
      })
  ), [teams, selectedTeams, isAdmin, sortBy, sortOrder]);

  if (activeTeams.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200" data-testid="crews-weekly-empty">
        <p className="text-gray-600">Selecione pelo menos uma equipe para visualizar por semana</p>
      </div>
    );
  }

  const weekLabel = (w) => (w.work_start ? `${short(w.work_start)} – ${short(w.work_end)}` : `Pgto ${full(w.week_ending)}`);
  const cellVal = (col, obj) => {
    const raw = obj[col.key] || 0;
    if (!col.money) return raw;
    if (col.neg) {
      const ad = obj.adicional_total || 0;
      const parts = [raw > 0 ? `- ${formatMoney(raw)}` : null, ad > 0 ? `+ ${formatMoney(ad)}` : null].filter(Boolean);
      return parts.length ? parts.join(' / ') : '-';
    }
    return formatMoney(raw);
  };
  const colColor = (col) => {
    if (col.key === 'total_final') return 'text-teal-700 font-semibold';
    if (col.key === 'lucro_empresa') return 'text-purple-700 font-semibold';
    if (col.key === 'total_expenses') return 'text-orange-600';
    if (col.key === 'backcharge_total') return 'text-yellow-700';
    return '';
  };

  // ---------- CARDS ----------
  if (viewMode === 'cards') {
    const cols = buildColumns(cardsFields, isAdmin);
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="crews-weekly-cards">
        {activeTeams.map((team) => {
          const weeks = buildTeamWeekly(team, weekMetaMap);
          const totals = {};
          cols.forEach((c) => { totals[c.key] = weeks.reduce((s, w) => s + (w[c.key] || 0), 0); });
          return (
            <div key={team.team_name} className="bg-white border rounded-lg shadow-sm overflow-hidden" data-testid={`crews-weekly-card-${team.team_name}`}>
              <div className="bg-gradient-to-r from-indigo-500 to-blue-500 text-white p-3 flex items-center justify-between">
                <h3 className="text-lg font-bold">{team.team_name}</h3>
                {cardsFields.porcentagem && isAdmin && (
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">{team.percentage}%</span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-indigo-50 text-indigo-900">
                      <th className="px-3 py-2 text-left">Semana</th>
                      {cols.map((c) => (
                        <th key={c.key} className="px-3 py-2 text-right whitespace-nowrap">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {weeks.length === 0 && (
                      <tr><td className="px-3 py-3 text-gray-400" colSpan={cols.length + 1}>Sem dados no período</td></tr>
                    )}
                    {weeks.map((w, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">{weekLabel(w)}</td>
                        {cols.map((c) => (
                          <td key={c.key} className={`px-3 py-2 text-right whitespace-nowrap ${colColor(c)}`}>{cellVal(c, w)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {weeks.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                        <td className="px-3 py-2">TOTAL</td>
                        {cols.map((c) => (
                          <td key={c.key} className={`px-3 py-2 text-right whitespace-nowrap ${colColor(c)}`}>{cellVal(c, totals)}</td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ---------- TABELA ----------
  if (viewMode === 'table') {
    const cols = buildColumns(tableFields, isAdmin);
    return (
      <div className="space-y-4" data-testid="crews-weekly-table">
        {activeTeams.map((team) => {
          const weeks = buildTeamWeekly(team, weekMetaMap);
          const totals = {};
          cols.forEach((c) => { totals[c.key] = weeks.reduce((s, w) => s + (w[c.key] || 0), 0); });
          return (
            <div key={team.team_name} className="bg-white border rounded-lg overflow-hidden" data-testid={`crews-weekly-table-${team.team_name}`}>
              <div className="bg-blue-600 text-white px-4 py-2 flex items-center justify-between">
                <span className="font-bold">{team.team_name}</span>
                {tableFields.porcentagem && isAdmin && <span className="text-xs">{team.percentage}%</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-blue-50 text-blue-900">
                    <tr>
                      <th className="px-4 py-2 text-left">Semana (trabalho)</th>
                      <th className="px-4 py-2 text-center">Pgto</th>
                      {cols.map((c) => (
                        <th key={c.key} className="px-4 py-2 text-right whitespace-nowrap">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {weeks.length === 0 && (
                      <tr><td className="px-4 py-3 text-gray-400" colSpan={cols.length + 2}>Sem dados no período</td></tr>
                    )}
                    {weeks.map((w, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium whitespace-nowrap">{weekLabel(w)}</td>
                        <td className="px-4 py-2 text-center text-gray-500 whitespace-nowrap">{full(w.week_ending)}</td>
                        {cols.map((c) => (
                          <td key={c.key} className={`px-4 py-2 text-right whitespace-nowrap ${colColor(c)}`}>{cellVal(c, w)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {weeks.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                        <td className="px-4 py-2" colSpan={2}>TOTAL</td>
                        {cols.map((c) => (
                          <td key={c.key} className={`px-4 py-2 text-right whitespace-nowrap ${colColor(c)}`}>{cellVal(c, totals)}</td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ---------- RANKING (um ranking por semana) ----------
  if (viewMode === 'ranking') {
    const metricMap = {
      lucro_empresa: 'lucro_empresa',
      total_final: 'total_final',
      total_jobs: 'trabalhos',
      total_labor: 'total_labor',
      total_expenses: 'total_expenses',
    };
    const metricField = metricMap[sortBy] || 'total_final';
    const metricLabel = {
      lucro_empresa: 'Lucro (Empresa)', total_final: 'Total Final', trabalhos: 'Trabalhos',
      total_labor: 'Total Labor', total_expenses: 'Despesas',
    }[metricField] || 'Total Final';
    const isMoney = metricField !== 'trabalhos';

    // pré-computa semanas por equipe
    const perTeam = activeTeams.map((t) => ({ team: t, byWeek: (() => {
      const map = {}; buildTeamWeekly(t, weekMetaMap).forEach((w) => { map[w.week_ending] = w; }); return map;
    })() }));

    return (
      <div className="space-y-5" data-testid="crews-weekly-ranking">
        {orderedWeeks.map((wk) => {
          const meta = weekMetaMap[wk] || {};
          const label = meta.work_start ? `${short(meta.work_start)} – ${short(meta.work_end)}` : `Pgto ${full(wk)}`;
          const rows = perTeam
            .map(({ team, byWeek }) => ({ team_name: team.team_name, value: (byWeek[wk] && byWeek[wk][metricField]) || 0, obj: byWeek[wk] }))
            .filter((r) => r.obj)
            .sort((a, b) => sortOrder === 'desc' ? b.value - a.value : a.value - b.value);
          if (rows.length === 0) return null;
          return (
            <div key={wk} className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-300 rounded-lg p-4">
              <h4 className="font-bold text-yellow-800 mb-3 flex items-center justify-between">
                <span>🗓️ {label}</span>
                <span className="text-xs font-normal text-gray-600">Pgto {full(wk)} · {metricLabel}</span>
              </h4>
              <div className="space-y-2">
                {rows.map((r, index) => (
                  <div key={r.team_name} className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
                    index === 0 ? 'border-yellow-400 bg-yellow-50' : index === 1 ? 'border-gray-400 bg-gray-50' : index === 2 ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'
                  }`}>
                    <div className="text-2xl font-bold text-gray-400 w-10 text-center">
                      {index < 3 ? (index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉') : `#${index + 1}`}
                    </div>
                    <div className="flex-1 font-bold text-gray-800">{r.team_name}</div>
                    <div className="text-lg font-bold text-green-600">{isMoney ? formatMoney(r.value) : r.value}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

export default CrewsWeeklyView;
